import {
  AuthApiPath,
  CONTENT_TYPE_FORM,
  REQUIRES_SLASH,
} from "@/lib/auth/constants";
import { buildApiEndpoint } from "@/lib/auth/buildApiEndpoint";
import { QueryParams } from "@/utils/functions/buildSafeUrl";
import { requestAndBindCsrfToHeaders } from "@/lib/auth/requestAndBindCsrfToHeaders";
import * as QueryString from "querystring";
import newrelic from "@/lib/monitoring/newrelic";

export interface ResetPasswordPayload {
  new_password1: string;
  new_password2: string;
  [key: string]: string;
}

export interface ResetPasswordResponse {
  reset_status: boolean; // true = success, false = fail
  err_msg?: string;
  token_invalid?: boolean;
  [key: string]: any; // for future extensibility
}

/**
 * Reset a user's password using a token
 * @param payload The new password and any additional fields
 * @param token The password reset token
 * @param isAccountRecovery Optional flag for account recovery
 */
export default async function resetPassword(
  payload: ResetPasswordPayload,
  token: string,
  isAccountRecovery: boolean = true,
): Promise<ResetPasswordResponse> {
  const startTime = Date.now();
  let qps: QueryParams = {} as QueryParams;

  if (qps) {
    qps["is_account_recovery"] = "true";
  }

  let url = buildApiEndpoint(AuthApiPath.PasswordReset, {
    additionalPaths: [token],
    qps,
    endingSlash: REQUIRES_SLASH.includes(AuthApiPath.PasswordReset),
  });

  const headers = await requestAndBindCsrfToHeaders({
    "Content-Type": CONTENT_TYPE_FORM,
  });

  const body = QueryString.stringify(payload);

  const res = await fetch(url, {
    method: "POST",
    headers,
    body,
    credentials: "include",
  });
  const responseTime = Date.now() - startTime;

  if (!res.ok) {
    newrelic.noticeError(new Error(`Password reset failed: ${res.status}`), {
      submodule: "auth",
      endpoint: "PasswordReset",
      status: res.status,
      errorMessage: "Password reset failed",
      responseTime: responseTime,
      hasToken: !!token,
      isAccountRecovery,
      timestamp: new Date().toISOString(),
    });
    throw new Error(`Password reset failed: ${res.status}`);
  }
  return res.json();
}
